// ============================================================================
// SignupPage — the expanded trainer registration (route "/signup", ns "signup").
// ----------------------------------------------------------------------------
// A premium, mobile-first "create account" screen that matches the bold Pulse
// sign-in aesthetic (so /login and /signup read as one family). Composition
// lives in app/signup/*; this file orchestrates the split layout, the
// already-authed redirect, and the destination wiring.
//
// • SIGNUP — POSTs to /api/auth/signup through useAuth().signup (the server
//   creates the account, sets the httponly vf_session cookie, and emails a
//   verification link when an email was given). On success we either prompt to
//   verify the email (reusing the Wave-6 verify posture) or send the new trainer
//   straight to their studio.
// • DESIGN — built entirely on the LOCKED Pulse design system; nothing in the
//   design system is restyled, this page only composes it.
// • MOBILE-FIRST — designed at 390 first (compact brand band leads, the form
//   sits below); from lg up it becomes a two-up split (brand stage left, form
//   right).
// • MOTION — Framer Motion throughout, reduced-motion aware.
// ============================================================================
import { Navigate, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { LanguageToggle } from "../i18n/LanguageToggle";
import { SignupAside } from "./signup/SignupAside";
import { SignupForm } from "./signup/SignupForm";

interface FromState {
  from?: { pathname?: string };
}

function BrandMark() {
  const { t } = useTranslation();
  return (
    <Link to="/" className="group inline-flex items-center gap-2.5">
      <span className="grid h-9 w-9 place-items-center rounded-md bg-grad-coral shadow-volt">
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
          <path
            d="M3 13h4l2-6 3 11 2.6-8L18 13h3"
            stroke="#fff"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="font-display text-lg font-extrabold tracking-tightest text-body">
        {t("app.name", { ns: "common" })}
      </span>
    </Link>
  );
}

export function SignupPage() {
  const { t, ready } = useTranslation("signup");
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  const dest = (location.state as FromState | null)?.from?.pathname ?? "/app";

  // Already signed in? Skip the form entirely.
  if (!isLoading && isAuthenticated) return <Navigate to={dest} replace />;

  if (!ready) return <div className="min-h-screen bg-paper" aria-hidden />;

  return (
    <div className="min-h-screen bg-paper text-body lg:grid lg:grid-cols-[1.05fr_0.95fr]">
      {/* Brand stage — a compact band on mobile, the full left column on lg. */}
      <SignupAside />

      {/* Form column. */}
      <div className="relative flex min-h-screen flex-col bg-surface lg:min-h-0">
        <header className="flex items-center justify-between px-gutter py-5 lg:py-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 font-display text-sm font-bold text-body-soft transition-colors hover:text-body"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
              <path
                d="M15 6l-6 6 6 6"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t("back")}
          </Link>
          <LanguageToggle />
        </header>

        <main className="flex flex-1 flex-col items-center justify-center px-gutter pb-12 pt-2 sm:pt-4">
          <div className="mb-7 self-start sm:self-center lg:self-start lg:max-w-md lg:w-full">
            <BrandMark />
            <p className="mt-1 text-sm text-body-soft">
              {t("app.tagline", { ns: "common" })}
            </p>
          </div>
          <SignupForm dest={dest} />
        </main>
      </div>
    </div>
  );
}
